import InfoIcon from '@mui/icons-material/Info'
import SettingsIcon from '@mui/icons-material/Settings'
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid2 as Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  useTheme,
} from '@mui/material'
import AnimateButton from 'components/@extended/AnimateButton'
import Card from 'components/@extended/Card'
import { TextField } from 'components/@extended/TextField'
import ComponentField from 'components/ComponentField'
import SimpleTitle from 'components/SimpleTitle'
import SingleOrganizationSelector from 'components/SingleOrganizationSelector'
import { useGlobalState } from 'contexts/GlobalStateContext'
import { postWithToken } from 'hooks/http'
import useAuth from 'hooks/useAuth'
import { useGetComponentLabels } from 'hooks/useComponents' // Import the new hook
import { useFetchOrganizationCoreSettings } from 'hooks/useOrganizations'
import { useSnackbar } from 'notistack'
import type { FC } from 'react'
import { startTransition, useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ComponentSourceType,
  ComponentType,
  CompositeFunction,
  CompositeType,
  getInputFormatFromComponentType,
} from 'types/components'
import { ThemeMode } from 'types/config'

const CreateComponent: FC = () => {
  const theme = useTheme()
  const { token, profile, isLoading: isLoadingProfile } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const { purcentAppState } = useGlobalState()

  const [selectedOrganization, setSelectedOrganization] = useState<string>('')
  const [label, setLabel] = useState<string>('')
  const [componentType, setComponentType] = useState<ComponentType>(ComponentType.COMPONENT_TYPE_FLOAT)
  const [isOrganizationLevel, setIsOrganizationLevel] = useState<boolean>(true)
  const [targetMin, setTargetMin] = useState<string>('')
  const [targetMax, setTargetMax] = useState<string>('')
  const [isFormValid, setIsFormValid] = useState<boolean>(false)
  const [isLabelUsed, setIsLabelUsed] = useState<boolean>(false)
  const [sourceType, setSourceType] = useState<ComponentSourceType>(ComponentSourceType.COMPONENT_SOURCE_MANUAL)
  const [compositeFunction, setCompositeFunction] = useState<CompositeFunction>(
    CompositeFunction.COMPOSITE_FUNCTION_NONE
  )
  const [compositeType, setCompositeType] = useState<CompositeType>(CompositeType.COMPOSITE_TYPE_SIMPLE)
  const [lowerIsBetter, setLowerIsBetter] = useState<boolean>(false)
  const [cumulativeTargets, setCumulativeTargets] = useState<boolean>(false)
  const [numericDecimalPlaces, setNumericDecimalPlaces] = useState<number>(2)
  const [precision, setPrecision] = useState<number>(2)
  const [showAdvancedSettings, setShowAdvancedSettings] = useState<boolean>(false)

  const { labels, isLoading: isLoadingLabels } = useGetComponentLabels(token!, selectedOrganization) // Use the new hook
  const { coreSettings, isLoading: isLoadingCoreSettings } = useFetchOrganizationCoreSettings(
    selectedOrganization ? [selectedOrganization] : [],
    token!
  )

  const validateForm = useCallback(() => {
    setIsFormValid(
      selectedOrganization !== '' &&
        label !== '' &&
        !isLabelUsed &&
        (sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE
          ? compositeFunction !== CompositeFunction.COMPOSITE_FUNCTION_NONE
          : true)
    )
  }, [selectedOrganization, label, isLabelUsed, sourceType, compositeFunction])

  useEffect(() => {
    validateForm()
  }, [validateForm])

  useEffect(() => {
    if (labels.includes(label)) {
      setIsLabelUsed(true)
    } else {
      setIsLabelUsed(false)
    }
  }, [label, labels])

  useEffect(() => {
    if (componentType === ComponentType.COMPONENT_TYPE_BOOL) {
      setTargetMin('true')
      setTargetMax('true')
    } else {
      setTargetMin('0')
      setTargetMax('0')
    }
  }, [componentType])

  // Auto-set prorateTargets based on component type
  useEffect(() => {
    const shouldBeCumulative =
      componentType === ComponentType.COMPONENT_TYPE_INTEGER ||
      componentType === ComponentType.COMPONENT_TYPE_CURRENCY ||
      componentType === ComponentType.COMPONENT_TYPE_FLOAT
    setCumulativeTargets(shouldBeCumulative)
  }, [componentType])

  // Helper to check if component type is numeric (float or integer)
  const isNumericType = (type: ComponentType) =>
    type === ComponentType.COMPONENT_TYPE_FLOAT || type === ComponentType.COMPONENT_TYPE_INTEGER

  // Auto-select Simple composite type when format is not percentage
  useEffect(() => {
    if (
      componentType !== ComponentType.COMPONENT_TYPE_PERCENTAGE &&
      compositeType === CompositeType.COMPOSITE_TYPE_COMPLEX
    ) {
      setCompositeType(CompositeType.COMPOSITE_TYPE_SIMPLE)
    }
  }, [componentType, compositeType])

  // Auto-select Average function when composite type is Complex
  useEffect(() => {
    if (compositeType === CompositeType.COMPOSITE_TYPE_COMPLEX) {
      setCompositeFunction(CompositeFunction.COMPOSITE_FUNCTION_AVERAGE)
    }
  }, [compositeType])

  const handleSubmit = () => {
    const componentDTO = {
      rootOrganizationId: purcentAppState?.selectedOrganization?.rootOrganizationId || '',
      organizationId: selectedOrganization,
      ledgerAccountId: null,
      isOrganizationLevel,
      isTargetEditable: true,
      restrictedVisibility: false,
      targetMin,
      targetMax,
      label,
      format: getInputFormatFromComponentType(componentType),
      sourceType: sourceType,
      componentType,
      lowerIsBetter,
      cumulativeTargets: cumulativeTargets,
      numericDecimalPlaces: isNumericType(componentType) ? numericDecimalPlaces : undefined,
      precision: isNumericType(componentType) ? precision : undefined,
      sourceInfo: JSON.stringify({
        compositeFunction:
          sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE
            ? compositeFunction
            : CompositeFunction.COMPOSITE_FUNCTION_NONE,
        compositeType: sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE ? compositeType : undefined,
      }),
    }

    postWithToken('components', token!, [componentDTO])
      .then(() => {
        enqueueSnackbar('Component created successfully', { variant: 'success' })
        startTransition(() => {
          navigate('/admin/components/manage')
        })
      })
      .catch((error) => {
        const errorMessage = error.message || 'Failed to create component'
        enqueueSnackbar(errorMessage, { variant: 'error' })
      })
  }

  return (
    <Box>
      <SimpleTitle title="Create Component" />
      <Box mb={3}>
        {isLoadingProfile || isLoadingLabels ? (
          <CircularProgress />
        ) : (
          <>
            <Box sx={{ maxWidth: 800, mx: 'auto', mb: 3 }}>
              <Card variant="outlined" sx={{ p: 4 }}>
                <Grid container spacing={3}>
                  {/* Row 1: Name */}
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label="Name *"
                      key="label-field"
                      variant="outlined"
                      pill
                      shrink
                      fullWidth
                      value={label}
                      onChange={(e) => {
                        setLabel(e.target.value)
                        validateForm()
                      }}
                      error={isLabelUsed}
                      helperText={isLabelUsed ? 'Label is already used' : ''}
                    />
                  </Grid>

                  {/* Row 2: Select Team */}
                  <Grid size={{ xs: 12 }}>
                    <SingleOrganizationSelector
                      userProfile={profile!}
                      selectedOrganizationId={selectedOrganization}
                      handleChange={(value) => {
                        setSelectedOrganization(value)
                        validateForm()
                      }}
                      label="Select Organization *"
                      labelBackgroundColor={theme.palette.background.paper}
                    />
                  </Grid>

                  {/* Row 3: Type */}
                  <Grid size={{ xs: 12 }}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel shrink variant="outlined" sx={{ backgroundColor: theme.palette.background.paper }}>
                        Type *
                      </InputLabel>
                      <Select
                        id="component-type-select"
                        value={isOrganizationLevel}
                        onChange={(e) => setIsOrganizationLevel(e.target.value === 'true')}
                        fullWidth
                        variant="outlined"
                        style={{ borderRadius: '21px' }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              '& .MuiMenuItem-root:hover': {
                                backgroundColor:
                                  theme.palette.mode === ThemeMode.DARK
                                    ? theme.palette.primary.darker
                                    : theme.palette.secondary.dark,
                              },
                            },
                          },
                        }}
                      >
                        <MenuItem value="true">Team</MenuItem>
                        <MenuItem value="false">Individual</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Row 4: Format with gear icon */}
                  <Grid size={{ xs: 12 }}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel shrink variant="outlined" sx={{ backgroundColor: theme.palette.background.paper }}>
                        Format *
                      </InputLabel>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Select
                          id="component-format-select"
                          value={componentType}
                          onChange={(e) => setComponentType(e.target.value as ComponentType)}
                          fullWidth
                          variant="outlined"
                          style={{ borderRadius: '21px' }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                '& .MuiMenuItem-root:hover': {
                                  backgroundColor:
                                    theme.palette.mode === ThemeMode.DARK
                                      ? theme.palette.primary.darker
                                      : theme.palette.secondary.dark,
                                },
                              },
                            },
                          }}
                        >
                          <MenuItem value={ComponentType.COMPONENT_TYPE_FLOAT}>Numeric</MenuItem>
                          <MenuItem value={ComponentType.COMPONENT_TYPE_CURRENCY}>Currency</MenuItem>
                          <MenuItem value={ComponentType.COMPONENT_TYPE_PERCENTAGE}>Percentage</MenuItem>
                          <MenuItem value={ComponentType.COMPONENT_TYPE_BOOL}>Boolean</MenuItem>
                        </Select>
                        <Tooltip title="Advanced Settings">
                          <span>
                            <IconButton
                              onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                              disabled={componentType === ComponentType.COMPONENT_TYPE_BOOL}
                              color={showAdvancedSettings ? 'primary' : 'default'}
                            >
                              <SettingsIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </FormControl>
                  </Grid>

                  {/* Advanced Settings - Only show if showAdvancedSettings is true and not Boolean */}
                  {showAdvancedSettings && componentType !== ComponentType.COMPONENT_TYPE_BOOL && (
                    <>
                      {/* Row 5: Source Type */}
                      <Grid size={{ xs: 12 }}>
                        <FormControl fullWidth variant="outlined">
                          <InputLabel
                            shrink
                            variant="outlined"
                            sx={{ backgroundColor: theme.palette.background.paper }}
                          >
                            Source Type *
                          </InputLabel>
                          <Select
                            id="component-source-type-select"
                            value={sourceType}
                            onChange={(e) => {
                              setSourceType(e.target.value as ComponentSourceType)
                            }}
                            fullWidth
                            variant="outlined"
                            style={{ borderRadius: '21px' }}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  '& .MuiMenuItem-root:hover': {
                                    backgroundColor:
                                      theme.palette.mode === ThemeMode.DARK
                                        ? theme.palette.primary.darker
                                        : theme.palette.secondary.dark,
                                  },
                                },
                              },
                            }}
                          >
                            <MenuItem value={ComponentSourceType.COMPONENT_SOURCE_MANUAL}>Standard</MenuItem>
                            <MenuItem value={ComponentSourceType.COMPONENT_SOURCE_COMPOSITE}>Composite</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Row 6: Decimal Places (Display) and Decimal Places (Precision) */}
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth variant="outlined">
                          <InputLabel
                            shrink
                            variant="outlined"
                            sx={{ backgroundColor: theme.palette.background.paper }}
                          >
                            Decimal Places (Display)
                          </InputLabel>
                          <Select
                            id="numeric-decimal-places-select"
                            value={numericDecimalPlaces}
                            onChange={(e) => setNumericDecimalPlaces(Number(e.target.value))}
                            fullWidth
                            variant="outlined"
                            style={{ borderRadius: '21px' }}
                            disabled={
                              componentType !== ComponentType.COMPONENT_TYPE_FLOAT &&
                              componentType !== ComponentType.COMPONENT_TYPE_INTEGER &&
                              componentType !== ComponentType.COMPONENT_TYPE_CURRENCY &&
                              componentType !== ComponentType.COMPONENT_TYPE_PERCENTAGE
                            }
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  '& .MuiMenuItem-root:hover': {
                                    backgroundColor:
                                      theme.palette.mode === ThemeMode.DARK
                                        ? theme.palette.primary.darker
                                        : theme.palette.secondary.dark,
                                  },
                                },
                              },
                            }}
                          >
                            <MenuItem value={0}>0</MenuItem>
                            <MenuItem value={1}>1</MenuItem>
                            <MenuItem value={2}>2</MenuItem>
                            <MenuItem value={3}>3</MenuItem>
                            <MenuItem value={4}>4</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth variant="outlined">
                          <InputLabel
                            shrink
                            variant="outlined"
                            sx={{ backgroundColor: theme.palette.background.paper }}
                          >
                            Decimal Places (Precision)
                          </InputLabel>
                          <Select
                            id="numeric-precision-select"
                            value={precision}
                            onChange={(e) => setPrecision(Number(e.target.value))}
                            fullWidth
                            variant="outlined"
                            style={{ borderRadius: '21px' }}
                            disabled={
                              componentType !== ComponentType.COMPONENT_TYPE_FLOAT &&
                              componentType !== ComponentType.COMPONENT_TYPE_INTEGER &&
                              componentType !== ComponentType.COMPONENT_TYPE_CURRENCY &&
                              componentType !== ComponentType.COMPONENT_TYPE_PERCENTAGE
                            }
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  '& .MuiMenuItem-root:hover': {
                                    backgroundColor:
                                      theme.palette.mode === ThemeMode.DARK
                                        ? theme.palette.primary.darker
                                        : theme.palette.secondary.dark,
                                  },
                                },
                              },
                            }}
                          >
                            <MenuItem value={0}>0</MenuItem>
                            <MenuItem value={1}>1</MenuItem>
                            <MenuItem value={2}>2</MenuItem>
                            <MenuItem value={3}>3</MenuItem>
                            <MenuItem value={4}>4</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Row 7: Composite Function, Composite Type (Only Show row if source type is composite) */}
                      {sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE && (
                        <>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth variant="outlined">
                              <InputLabel
                                shrink
                                variant="outlined"
                                sx={{ backgroundColor: theme.palette.background.paper }}
                              >
                                Composite Function *
                              </InputLabel>
                              <Select
                                id="component-composite-function-select"
                                value={compositeFunction}
                                onChange={(e) => setCompositeFunction(e.target.value as CompositeFunction)}
                                fullWidth
                                variant="outlined"
                                style={{ borderRadius: '21px' }}
                                disabled={compositeType === CompositeType.COMPOSITE_TYPE_COMPLEX}
                                MenuProps={{
                                  PaperProps: {
                                    sx: {
                                      '& .MuiMenuItem-root:hover': {
                                        backgroundColor:
                                          theme.palette.mode === ThemeMode.DARK
                                            ? theme.palette.primary.darker
                                            : theme.palette.secondary.dark,
                                      },
                                    },
                                  },
                                }}
                              >
                                <MenuItem value={CompositeFunction.COMPOSITE_FUNCTION_AVERAGE}>Average</MenuItem>
                                <MenuItem value={CompositeFunction.COMPOSITE_FUNCTION_MEDIAN}>Median</MenuItem>
                                <MenuItem value={CompositeFunction.COMPOSITE_FUNCTION_MODE}>Mode</MenuItem>
                                <MenuItem value={CompositeFunction.COMPOSITE_FUNCTION_COUNT}>Count</MenuItem>
                                <MenuItem value={CompositeFunction.COMPOSITE_FUNCTION_STDDEV}>
                                  Standard Deviation
                                </MenuItem>
                                <MenuItem value={CompositeFunction.COMPOSITE_FUNCTION_SUM}>Sum</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth variant="outlined">
                              <InputLabel
                                shrink
                                variant="outlined"
                                sx={{ backgroundColor: theme.palette.background.paper }}
                              >
                                Composite Type *
                              </InputLabel>
                              <Select
                                id="component-composite-type-select"
                                value={compositeType}
                                onChange={(e) => setCompositeType(e.target.value as CompositeType)}
                                fullWidth
                                variant="outlined"
                                style={{ borderRadius: '21px' }}
                                MenuProps={{
                                  PaperProps: {
                                    sx: {
                                      '& .MuiMenuItem-root:hover': {
                                        backgroundColor:
                                          theme.palette.mode === ThemeMode.DARK
                                            ? theme.palette.primary.darker
                                            : theme.palette.secondary.dark,
                                      },
                                    },
                                  },
                                }}
                              >
                                <MenuItem value={CompositeType.COMPOSITE_TYPE_SIMPLE}>Simple</MenuItem>
                                <MenuItem
                                  value={CompositeType.COMPOSITE_TYPE_COMPLEX}
                                  disabled={componentType !== ComponentType.COMPONENT_TYPE_PERCENTAGE}
                                >
                                  Complex
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                        </>
                      )}

                      {/* Row 8: Lower Is Better, Cumulative Targets */}
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth variant="outlined">
                          <InputLabel
                            shrink
                            variant="outlined"
                            sx={{ backgroundColor: theme.palette.background.paper }}
                          >
                            Lower Is Better *
                          </InputLabel>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Select
                              id="component-lower-is-better-select"
                              value={lowerIsBetter}
                              onChange={(e) => setLowerIsBetter(e.target.value === 'true')}
                              fullWidth
                              variant="outlined"
                              style={{ borderRadius: '21px' }}
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    '& .MuiMenuItem-root:hover': {
                                      backgroundColor:
                                        theme.palette.mode === ThemeMode.DARK
                                          ? theme.palette.primary.darker
                                          : theme.palette.secondary.dark,
                                    },
                                  },
                                },
                              }}
                            >
                              <MenuItem value="true">Yes</MenuItem>
                              <MenuItem value="false">No</MenuItem>
                            </Select>
                            <Tooltip
                              title="Enable this when a lower value is considered better performance. For example, 'Number of Errors' or 'Response Time' where lower values indicate better results."
                              placement="right"
                            >
                              <InfoIcon fontSize="small" sx={{ ml: 1, color: theme.palette.grey[500] }} />
                            </Tooltip>
                          </Box>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth variant="outlined">
                          <InputLabel
                            shrink
                            variant="outlined"
                            sx={{ backgroundColor: theme.palette.background.paper }}
                          >
                            Cumulative Targets *
                          </InputLabel>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Select
                              id="component-prorate-targets-select"
                              value={cumulativeTargets}
                              onChange={(e) => setCumulativeTargets(e.target.value === 'true')}
                              fullWidth
                              variant="outlined"
                              style={{ borderRadius: '21px' }}
                              disabled={
                                componentType !== ComponentType.COMPONENT_TYPE_CURRENCY &&
                                componentType !== ComponentType.COMPONENT_TYPE_FLOAT &&
                                componentType !== ComponentType.COMPONENT_TYPE_INTEGER
                              }
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    '& .MuiMenuItem-root:hover': {
                                      backgroundColor:
                                        theme.palette.mode === ThemeMode.DARK
                                          ? theme.palette.primary.darker
                                          : theme.palette.secondary.dark,
                                    },
                                  },
                                },
                              }}
                            >
                              <MenuItem value="true">Yes</MenuItem>
                              <MenuItem value="false">No</MenuItem>
                            </Select>
                            <Tooltip
                              title="Enable cumulative for targets. Automatically set based on component type (Integer, Currency, Float components are cumulative by default)."
                              placement="right"
                            >
                              <InfoIcon fontSize="small" sx={{ ml: 1, color: theme.palette.grey[500] }} />
                            </Tooltip>
                          </Box>
                        </FormControl>
                      </Grid>

                      {/* Row 9: Target Min and Target Max (Optional) */}
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <ComponentField
                            componentType={componentType}
                            label="Target Min"
                            value={targetMin}
                            onChange={(value) => {
                              setTargetMin(value)
                              validateForm()
                            }}
                            name="targetMin"
                            disabled={!selectedOrganization || isLoadingCoreSettings}
                            currency={coreSettings?.[0]?.currency || 'USD'}
                            currencyDecimals={coreSettings?.[0]?.currencyDecimals || 0}
                            decimalPlaces={precision}
                            variant="outlined"
                            pill
                            shrink
                          />
                          <Tooltip
                            title="Optional: Used as a suggestion when creating plans. Leave blank if not needed."
                            placement="right"
                          >
                            <InfoIcon fontSize="small" sx={{ ml: 1, color: theme.palette.grey[500] }} />
                          </Tooltip>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <ComponentField
                            componentType={componentType}
                            label="Target Max"
                            value={targetMax}
                            onChange={(value) => {
                              setTargetMax(value)
                              validateForm()
                            }}
                            name="targetMax"
                            disabled={!selectedOrganization || isLoadingCoreSettings}
                            currency={coreSettings?.[0]?.currency || 'USD'}
                            currencyDecimals={coreSettings?.[0]?.currencyDecimals || 0}
                            decimalPlaces={precision}
                            variant="outlined"
                            pill
                            shrink
                          />
                          <Tooltip
                            title="Optional: Used as a suggestion when creating plans. Leave blank if not needed."
                            placement="right"
                          >
                            <InfoIcon fontSize="small" sx={{ ml: 1, color: theme.palette.grey[500] }} />
                          </Tooltip>
                        </Box>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Card>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ maxWidth: 800, mx: 'auto' }}>
              <Stack direction="row" justifyContent="space-between" spacing={2}>
                <AnimateButton>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={() => {
                      startTransition(() => {
                        navigate('/admin/components/manage')
                      })
                    }}
                  >
                    Cancel
                  </Button>
                </AnimateButton>
                <AnimateButton>
                  <Button variant="contained" onClick={handleSubmit} disabled={!isFormValid}>
                    Create Component
                  </Button>
                </AnimateButton>
              </Stack>
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}

export default CreateComponent
